import React, { createContext, useContext, useState, useEffect, useCallback, useMemo } from 'react';
import axios from 'axios';

const AuthContext = createContext(null);

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [token, setToken] = useState(localStorage.getItem('volt_token'));
  const [loading, setLoading] = useState(true);

  const api = useMemo(() => {
    const instance = axios.create({
      baseURL: process.env.REACT_APP_BACKEND_URL + '/api',
      headers: { 'Content-Type': 'application/json' }
    });
    instance.interceptors.request.use((config) => {
      const currentToken = localStorage.getItem('volt_token');
      if (currentToken) {
        config.headers.Authorization = `Bearer ${currentToken}`;
      }
      return config;
    });
    return instance;
  }, []);

  useEffect(() => {
    const initAuth = async () => {
      const savedToken = localStorage.getItem('volt_token');
      if (savedToken) {
        try {
          const response = await api.get('/auth/me');
          setUser(response.data);
          setToken(savedToken);
        } catch (error) {
          localStorage.removeItem('volt_token');
          setToken(null);
          setUser(null);
        }
      }
      setLoading(false);
    };
    initAuth();
  }, [api]);

  const login = useCallback(async (email, password) => {
    const response = await api.post('/auth/login', { email, password });
    const { token: newToken, user: userData } = response.data;
    localStorage.setItem('volt_token', newToken);
    setToken(newToken);
    setUser(userData);
    return userData;
  }, [api]);

  const register = useCallback(async (data, verificationCode = null) => {
    const response = await api.post('/auth/register', data, {
      params: verificationCode ? { verification_code: verificationCode } : {}
    });
    const { token: newToken, user: userData } = response.data;
    localStorage.setItem('volt_token', newToken);
    setToken(newToken);
    setUser(userData);
    return userData;
  }, [api]);

  const sendVerificationCode = useCallback(async (email) => {
    const response = await api.post('/auth/send-verification-code', { email });
    return response.data;
  }, [api]);

  const verifyCode = useCallback(async (email, code) => {
    const response = await api.post('/auth/verify-code', { email, code });
    return response.data;
  }, [api]);

  const logout = useCallback(() => {
    localStorage.removeItem('volt_token');
    setToken(null);
    setUser(null);
  }, []);

  const updateUser = useCallback((userData) => {
    setUser(prev => ({ ...prev, ...userData }));
  }, []);

  const value = useMemo(() => ({
    user, token, loading, login, register, sendVerificationCode, verifyCode, logout, updateUser, api
  }), [user, token, loading, login, register, sendVerificationCode, verifyCode, logout, updateUser, api]);

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
};
